import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { CarLoader } from '@/components/common/CarLoader'

interface LoaderProps {
  className?: string
  size?: 'sm' | 'md' | 'lg'
  text?: string
}

/** Spinner for short, indeterminate waits. Use <Skeleton> blocks when the final layout is known. */
export function Loader({ className, size = 'md', text }: LoaderProps) {
  const sizeClasses = {
    sm: 'size-4 border-2',
    md: 'size-5 border-2',
    lg: 'size-5 border-2',
  }

  return (
    <div role="status" className={cn('flex flex-col items-center justify-center gap-2', className)}>
      <div
        aria-hidden="true"
        className={cn('animate-spin rounded-full border-border-strong border-t-primary', sizeClasses[size])}
      />
      {text ? <p className="text-base text-text-muted">{text}</p> : <span className="sr-only">Loading</span>}
    </div>
  )
}

export function PageLoader() {
  return (
    <div className="flex h-[calc(100vh-var(--topbar-h))] items-center justify-center">
      <CarLoader />
    </div>
  )
}

/** Table-shaped skeleton: same row height as DataTable so nothing jumps on load. */
export function TableSkeleton({ rows = 8, label = 'Loading' }: { rows?: number; label?: string }) {
  return (
    <div aria-busy="true" className="flex flex-col">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex h-row items-center gap-4 border-b border-border px-3 last:border-b-0">
          <Skeleton className="w-1/4" />
          <Skeleton className="w-1/6" />
          <Skeleton className="w-1/5" />
          <Skeleton className="ml-auto w-1/12" />
        </div>
      ))}
    </div>
  )
}
